import React, { useState, useEffect } from "react";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import ContentHeader from "../../components/ContentHeader";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./ColorCustomizer.scss";

const ColorCustomizer = () => {
	const defaultColors = {
		barBackground: "#5007AA",
		barText: "#FFFFFF",
		button: "#D1C1FF",
		buttonText: "#000000",
	};

	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isSaved, setIsSaved] = useState(false);
	const [colors, setColors] = useState(defaultColors);
	const [originalColors, setOriginalColors] = useState(defaultColors);
	const [hasChanges, setHasChanges] = useState(false);

	// Fetch initial colors
	useEffect(() => {
		fetchColors();
	}, []);

	// Track changes
	useEffect(() => {
		if (!isLoading) {
			const hasChanged =
				originalColors.barBackground !== colors.barBackground ||
				originalColors.barText !== colors.barText ||
				originalColors.button !== colors.button ||
				originalColors.buttonText !== colors.buttonText;

			setHasChanges(hasChanged);
		}
	}, [colors, originalColors, isLoading]);

	const fetchColors = async () => {
		setIsLoading(true);
		try {
			const data = await apiFetch({
				path: "/plac/v1/colors",
			});
			setColors(data);
			setOriginalColors(data);
		} catch (err) {
			console.error("Error fetching colors:", err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleColorChange = (key, value) => {
		setColors((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const saveColors = async () => {
		setIsSaving(true);
		setIsSaved(false);

		try {
			await apiFetch({
				path: "/plac/v1/colors",
				method: "POST",
				data: {
					...colors,
					_wpnonce: window.placAdminData.nonce,
				},
			});

			// Update original values after saving
			setOriginalColors({ ...colors });
			setHasChanges(false);

			// Show success message for 1.5s
			setTimeout(() => {
				setIsSaving(false);
				setIsSaved(true);

				// Reset success message after 1.5s
				setTimeout(() => {
					setIsSaved(false);
				}, 1500);
			}, 500);
		} catch (err) {
			console.error("Error saving colors:", err);
			setIsSaving(false);
		}
	};

	const discardChanges = () => {
		setColors({ ...originalColors });
		setHasChanges(false);
	};

	const ColorInput = ({
		label,
		icon,
		value,
		onChange,
		tooltipTitle,
		tooltipId,
	}) => (
		<div className="inputContainer">
			<OverlayTrigger
				placement="top"
				overlay={<Tooltip id={tooltipId}>{tooltipTitle}</Tooltip>}
			>
				<img src={icon} alt="icon" className="icon" />
			</OverlayTrigger>
			<div className="inputField">
				<div className="inputLabel header-1 primary-text-color semi-bold">{label}</div>
				<div className="colorPickerContainer">
					<div
						className="colorPreview"
						style={{
							position: "relative",
							display: "inline-flex",
							alignItems: "center",
						}}
					>
						<input
							type="color"
							value={value}
							onChange={(e) => onChange(e.target.value)}
							style={{
								opacity: 0,
								position: "absolute",
								left: 0,
								top: 0,
								width: "24px",
								height: "24px",
								cursor: "pointer",
							}}
						/>
						<div
							className="colorBox"
							style={{
								backgroundColor: value,
								width: "24px",
								height: "24px",
								border: "1px solid #ddd",
								borderRadius: "4px",
								marginRight: "8px",
							}}
						/>
						<span className="colorValue">{value}</span>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<>
		
			<div className="settings-container">
				<ContentHeader
					title={"Customize Access Control Switcher Design"}
					description={
						"Specify the colors of the background of the frontend admin switcher as well as the text color. Also select specific color that you want to set for the switch button, button text and more."
					}
				/>

				<div className="content-section">
					<div className="leftSection">
						<div className="sectionTitle header-1 primary-text-color semi-bold">
							{__("Color Customizer", "access-control")}
						</div>
						<div className="description header-2 primary-text-color regular">
							{__(
								"Specify the colors of the background of the frontend admin switcher as well as the text color. Also select specific color that you want to set for the switch button, button text and more.",
								"access-control",
							)}
						</div>
					</div>

					<div className="rightSection">
						{isLoading ? (
							<div className="loading-placeholder">
								{__("Loading color settings...", "access-control")}
							</div>
						) : (
							<>
								<ColorInput
									label="Bar Background Color"
									tooltipTitle={__(
										"Change Bar Background color",
										"access-control",
									)}
									tooltipId="change-bar-background-tooltip"
									icon={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
									value={colors.barBackground}
									onChange={(value) =>
										handleColorChange(
											"barBackground",
											value,
										)
									}
								/>
								<ColorInput
									label="Bar Text Color"
									tooltipTitle={__(
										"Change Bar Text color",
										"access-control",
									)}
									tooltipId="change-bar-text-tooltip"
									icon={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
									value={colors.barText}
									onChange={(value) =>
										handleColorChange("barText", value)
									}
								/>
								<ColorInput
									label="Button Color"
									tooltipTitle={__(
										"Change Button Background color",
										"access-control",
									)}
									tooltipId="change-button-tooltip"
									icon={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
									value={colors.button}
									onChange={(value) =>
										handleColorChange("button", value)
									}
								/>
								<ColorInput
									label="Button Text Color"
									tooltipTitle={__(
										"Change Button Text color",
										"access-control",
									)}
									tooltipId="change-button-text-tooltip"
									icon={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
									value={colors.buttonText}
									onChange={(value) =>
										handleColorChange("buttonText", value)
									}
								/>

								<div className="note">
									{__(
										"Note: Click on any color to open the color picker and choose your desired color.",
										"access-control",
									)}
								</div>
							</>
						)}
					</div>
				</div>

				<div className="footer-section">
					<button
						className={`save-button purple-bg-40 secondary-text-color ${
							!hasChanges ? "disabled" : ""
						}`}
						disabled={isLoading || isSaving || !hasChanges}
						onClick={saveColors}
					>
						{isSaving ? (
							<div className="saving-changes secondary-text-color">
								<div className="pl-access-control-loading-spinner"></div>
								{__("Changes Saving...", "access-control")}
							</div>
						) : isSaved ? (
							<div className="success-checkmark secondary-text-color">
								<img
									src={`${window.placAdminData.pluginUrl}/assets/images/success-tick.svg`}
									alt="success-icon"
								/>
								{__("Changes Saved", "access-control")}
							</div>
						) : (
							__("Save Changes", "access-control")
						)}
					</button>
					<button
						className={`discard-button purple-text-color btn-border ${
							!hasChanges ? "disabled" : ""
						}`}
						disabled={isLoading || isSaving || !hasChanges}
						onClick={discardChanges}
					>
						{__("Discard Changes", "access-control")}
					</button>

					{hasChanges && (
						<div className="unsaved-changes">
							<img
								src={`${window.placAdminData.pluginUrl}/assets/images/quotation.svg`}
								alt="quotation-icon"
							/>
							{__("Unsaved Changes", "access-control")}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default ColorCustomizer;
